import React from "react";
import { Check, MessageCircle, Star, CalendarDays, Tag, Plus, Info } from "lucide-react";
import { MESSENGER_URL, RATE_TIERS, RATE_ADD_ONS, LONG_STAY_DISCOUNTS } from "@/data/staycation";
import Seo from "@/components/Seo";

export default function Rates() {
  return (
    <div data-testid="rates-page">
      <Seo
        title="Rates — From ₱1,750 per night, no hidden fees"
        description="Transparent staycation rates: Studio from ₱1,750/night, 1-BR from ₱1,750, 2-BR Premium from ₱2,500. Long-stay discounts, no security deposit, includes Wi-Fi, kitchen and pool access."
        path="/rates"
      />
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pt-14 pb-10">
        <p className="xs-eyebrow">Transparent Rates</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black mt-2 max-w-2xl">Honest pricing, zero surprises.</h1>
        <p className="mt-4 text-neutral-600 max-w-xl">All rates below are starting prices per night. Final confirmation and any peak-season adjustments are sent via Messenger.</p>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {RATE_TIERS.map((tier, idx) => {
            const isFeatured = tier.badge === "Family favorite";
            return (
              <div
                key={tier.name}
                className={`relative rounded-3xl border p-6 flex flex-col ${
                  isFeatured
                    ? "border-[var(--xs-primary)] bg-white shadow-[0_30px_60px_-30px_rgba(255,56,92,0.35)]"
                    : "border-neutral-200 bg-white"
                }`}
                data-testid={`rate-card-${tier.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {tier.badge && (
                  <span className={`absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${isFeatured ? "bg-[var(--xs-primary)] text-white" : "bg-neutral-900 text-white"}`}>
                    <Star className="h-3 w-3 fill-current" /> {tier.badge}
                  </span>
                )}
                <h3 className="font-display text-xl font-bold leading-tight">{tier.name}</h3>
                <p className="mt-1 text-sm text-neutral-600">{tier.description}</p>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-xs text-neutral-500">from</span>
                  <span className="font-display text-3xl font-black text-[var(--xs-primary)]">₱{tier.nightly.toLocaleString()}</span>
                  <span className="text-sm text-neutral-500">{tier.suffix || "/ night"}</span>
                </div>
                <p className="mt-2 text-xs text-neutral-500">{tier.locations}</p>
                <ul className="mt-5 space-y-2 text-sm flex-1">
                  {tier.includes.map((inc) => (
                    <li key={inc} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[var(--xs-primary)] mt-0.5 shrink-0" />
                      <span className="text-neutral-700">{inc}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={MESSENGER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`rate-cta-${tier.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-colors ${
                    isFeatured ? "xs-btn-primary" : "bg-neutral-900 text-white hover:bg-neutral-800"
                  }`}
                >
                  <MessageCircle className="h-4 w-4" /> Inquire on Messenger
                </a>
              </div>
            );
          })}
        </div>
      </section>

      {/* Optional add-ons */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-16" data-testid="rate-addons">
        <div className="grid lg:grid-cols-3 gap-6 items-stretch">
          <div className="lg:col-span-1">
            <p className="xs-eyebrow">Optional Add-ons</p>
            <h3 className="font-display text-3xl font-bold mt-2 leading-tight">Customize your stay.</h3>
            <p className="mt-3 text-sm text-neutral-600 leading-relaxed">A few extras you can add at booking. Pricing is per stay unless noted.</p>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
            {RATE_ADD_ONS.map((a) => (
              <div key={a.name} className="xs-card p-6 hover:translate-y-0" data-testid={`addon-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[var(--xs-primary-soft)]">
                    <Plus className="h-4 w-4 text-[var(--xs-primary)]" />
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-widest text-[var(--xs-primary)] text-right">{a.price}</span>
                </div>
                <h4 className="font-display text-lg font-bold mt-4">{a.name}</h4>
                <p className="mt-1 text-sm text-neutral-600 leading-relaxed">{a.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Long-stay discounts */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-16" data-testid="long-stay-discounts">
        <div className="rounded-3xl bg-[var(--xs-primary)] text-white p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute -bottom-20 -right-10 w-80 h-80 rounded-full bg-white/10 blur-3xl" aria-hidden />
          <div className="relative">
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-white/80 inline-flex items-center gap-2">
              <Tag className="h-3.5 w-3.5" /> Stay longer, save more
            </p>
            <h3 className="font-display text-3xl sm:text-4xl font-black mt-3 max-w-xl">Automatic discounts on multi-night stays.</h3>
            <p className="mt-3 text-white/90 max-w-xl">Booking for more than a few nights? You'll get an automatic discount on your total stay — just mention your dates when you message us.</p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {LONG_STAY_DISCOUNTS.map((d) => (
                <div
                  key={d.range}
                  className="rounded-2xl bg-white/10 backdrop-blur border border-white/20 p-5"
                  data-testid={`discount-tier-${d.percent}`}
                >
                  <div className="flex items-center gap-2 text-white/80">
                    <CalendarDays className="h-4 w-4" />
                    <span className="text-xs font-semibold uppercase tracking-wider">{d.label}</span>
                  </div>
                  <p className="font-display text-5xl font-black mt-3">{d.percent}%<span className="text-xl font-bold"> off</span></p>
                  <p className="mt-2 text-sm font-semibold">{d.range}</p>
                  <p className="mt-1 text-xs text-white/80">{d.note}</p>
                </div>
              ))}
            </div>

            <a
              href={MESSENGER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="discounts-messenger-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[var(--xs-primary)] hover:bg-white/90 px-6 py-3 text-sm font-semibold transition-colors"
            >
              <MessageCircle className="h-4 w-4" /> Ask about long-stay rates
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-24">
        <div className="rounded-3xl bg-[var(--xs-bg-alt)] border border-neutral-200 p-8 sm:p-12 grid md:grid-cols-2 gap-8">
          <div>
            <p className="xs-eyebrow">Good to know</p>
            <h3 className="font-display text-2xl font-bold mt-2">What's included in every stay</h3>
            <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
              We keep things simple — every booking gets the essentials so you can just unpack and relax. Reservation is secured via a 50% downpayment, with the balance due on check-in.
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm">
            {[
              "Free Wi-Fi",
              "Netflix TV",
              "Air-conditioning",
              "Hot & cold shower",
              "Bidet",
              "Queen size bed",
              "Sofa bed",
              "Automatic washing machine",
              "Refrigerator",
              "Kitchen utensils",
              "Fresh towels provided",
              "Guest kit (shampoo, soap, toothbrush, toothpaste)",
            ].map((x) => (
              <li key={x} className="flex items-start gap-2">
                <Check className="h-4 w-4 text-[var(--xs-primary)] mt-0.5 shrink-0" /> {x}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-24">
        <div className="rounded-3xl border border-neutral-200 bg-white p-6 sm:p-8 flex items-start gap-4" data-testid="rates-info-note">
          <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[var(--xs-primary-soft)]">
            <Info className="h-5 w-5 text-[var(--xs-primary)]" />
          </div>
          <div className="text-sm text-neutral-700 leading-relaxed">
            <p className="font-display text-base font-bold text-neutral-900">Extras at a glance</p>
            <p className="mt-1">Pool access <strong>+₱150/pax</strong> · Basement parking <strong>+₱700/night</strong> (subject to availability) · Guest & baggage assistance <strong>+₱200</strong> · Early check-in / late check-out <strong>+₱1,000</strong> (subject to availability). Mention any of these in your Messenger inquiry and we'll bundle them into your booking summary.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
